import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import type { TypographyVariant } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import { DesignAreaField } from '../components/DesignAreaField';
import {
  useBuildingCandidateStoreyCount,
  useSetBuildingCandidateStoreyCount,
} from '../store/design-store.hooks';
import { DEFAULT_NEW_BUILDING_STOREY_COUNT } from '../store/design.store';
import { IconButton } from '@ui/buttons';

const STOREY_COUNT_INVALID_MESSAGE = 'Enter at least 1 storey';

type BuildingStoreyCountInputProps = {
  variant?: TypographyVariant;
};

const useStyles = makeStyles()({
  steppers: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: 2,
  },
});

/** Storey-count input shown only while an empty building candidate is active. */
export const BuildingStoreyCountInput: React.FC<
  BuildingStoreyCountInputProps
> = ({ variant = 'h6' }) => {
  const storeyCount =
    useBuildingCandidateStoreyCount() ?? DEFAULT_NEW_BUILDING_STOREY_COUNT;
  const setStoreyCount = useSetBuildingCandidateStoreyCount();
  const { enqueueSnackbar } = useSnackbar();
  const { classes } = useStyles();
  const [isEditing, startEditing, stopEditing] = useBooleanState(false);
  const [reselectToken, setReselectToken] = useState(0);
  const [draftValue, setDraftValue] = useState(String(storeyCount));

  useEffect(() => setDraftValue(String(storeyCount)), [storeyCount]);

  const handleSave = useCallback(
    (value: string) => {
      const nextValue = Number(value);
      if (!Number.isSafeInteger(nextValue)) {
        enqueueSnackbar(STOREY_COUNT_INVALID_MESSAGE, { variant: 'error' });
        setReselectToken((token) => token + 1);
        return;
      }
      const normalizedValue = Math.max(1, nextValue);
      setStoreyCount(normalizedValue);
      setDraftValue(String(normalizedValue));
      stopEditing();
    },
    [enqueueSnackbar, setStoreyCount, stopEditing],
  );

  const formatInput = useCallback(
    (value: string) => value.replace(/\D/g, ''),
    [],
  );

  const handleStep = useCallback(
    (step: -1 | 1) => {
      const parsedDraft = Number(draftValue);
      const currentValue = Number.isSafeInteger(parsedDraft)
        ? parsedDraft
        : storeyCount;
      const nextValue = Math.max(1, currentValue + step);
      setStoreyCount(nextValue);
      setDraftValue(String(nextValue));
      stopEditing();
    },
    [draftValue, setStoreyCount, stopEditing, storeyCount],
  );

  const steppers = (
    <Box className={classes.steppers}>
      <IconButton
        category="mui"
        icon="KeyboardArrowUp"
        size="small"
        shape="square"
        aria-label="Increase storeys"
        sx={{ width: 16, height: 10, padding: 0 }}
        onClick={() => handleStep(1)}
      />
      <IconButton
        category="mui"
        icon="KeyboardArrowDown"
        size="small"
        shape="square"
        aria-label="Decrease storeys"
        sx={{ width: 16, height: 10, padding: 0 }}
        onClick={() => handleStep(-1)}
      />
    </Box>
  );

  return (
    <DesignAreaField
      label="Storeys"
      align="left"
      unit={null}
      variant={variant}
      value={String(storeyCount)}
      defaultValue={String(storeyCount)}
      editing={isEditing}
      selectOnFocus
      reselectToken={reselectToken}
      valueEndAdornment={steppers}
      onClick={startEditing}
      onChange={setDraftValue}
      onSave={handleSave}
      onCancel={stopEditing}
      formatInput={formatInput}
    />
  );
};
